import React, { useEffect, useState, Component } from "react";
import Box from "@carrier-io/air-react/Box";
import DocViewer, { DocViewerRenderers } from "react-doc-viewer";
import { FC, useMemo } from "react";
import Grid from "@carrier-io/air-react/Grid";
import Link from "@carrier-io/air-react/Link";
import { Alert } from "@carrier-io/air-react";

interface ConsentRenderFileContentProps {
  fileUrl: string;
}

const SUPPORTED_FILE_FORMATS = [
  "bmp",
  "doc",
  "docx",
  "htm",
  "html",
  "jpg",
  "jpeg",
  "pdf",
  "png",
  "ppt",
  "pptx",
  "tiff",
  "txt",
  "xls",
  "xlsx",
];

class ErrorBoundaryWrapper extends Component<{
  children: React.ReactNode;
  onError: () => React.ReactNode;
}> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return this.props.onError();
    }

    return this.props.children;
  }
}

export const ConsentRenderFileContent: FC<ConsentRenderFileContentProps> = ({
  fileUrl,
}) => {
  const [isCorsCompatible, setIsCorsCompatible] = useState(false);

  const fileFormat = useMemo(() => {
    const extension = fileUrl.split(".").pop()?.toLowerCase();

    return (
      SUPPORTED_FILE_FORMATS.find((format) => format === extension) || "html"
    );
  }, [fileUrl]);

  useEffect(() => {
    const checkCors = async () => {
      try {
        const fileUrlObj = new URL(fileUrl);

        if (window.location.host === fileUrlObj.host) {
          setIsCorsCompatible(true);
          return;
        }

        const response = await fetch(fileUrl, { method: "HEAD" });
        setIsCorsCompatible(response.ok);
      } catch (error) {
        setIsCorsCompatible(false);
      }
    };
    checkCors();
  }, [fileUrl]);

  const FallbackComponent = () => (
    <Box sx={{ padding: 2 }}>
      <Alert role="alert" severity="info" variant="outlined">
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid>Please download the file to view it.</Grid>
          <Grid sx={{ marginLeft: "auto" }}>
            <Link
              color="primary"
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download
            </Link>
          </Grid>
        </Grid>
      </Alert>
    </Box>
  );

  console.log({ fileUrl, fileFormat, isCorsCompatible });
  if (!fileFormat || !isCorsCompatible) {
    return <FallbackComponent />;
  }

  if (fileFormat === "html" || fileFormat === "htm") {
    return (
      <ErrorBoundaryWrapper onError={() => <FallbackComponent />}>
        <Box>
          <iframe
            src={fileUrl}
            width="100%"
            height="500px"
            frameBorder="0"
            style={{ border: "none" }}
            title="Document Viewer"
            sandbox="allow-same-origin allow-scripts"
          />
        </Box>
      </ErrorBoundaryWrapper>
    );
  }

  return (
    <ErrorBoundaryWrapper onError={() => <FallbackComponent />}>
      <Box sx={{ height: "500px", width: "100%" }}>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <DocViewer
          documents={[{ uri: fileUrl }]}
          pluginRenderers={DocViewerRenderers}
          config={{
            header: {
              disableHeader: true,
              disableFileName: true,
              retainURLParams: false,
            },
          }}
        />
      </Box>
    </ErrorBoundaryWrapper>
  );
};
